import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Navigate } from 'react-router-dom';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import Th from '../../components/Th';
import FilledButton from '../../components/Buttons/FilledButton';
import Textarea from '../../components/Textarea';
import UserService from '../../services/UserService';
import SuperAdminService from '../../services/SuperAdminService';
import { USER_ROLES } from '../../constants/main';
import { UiContext } from '../../context/UiContext';
import { useUiStore } from '../../store';

import classes from './styles.module.scss';

const ITEMS_PER_PAGE = 10;

export default function SuperAdminHelpCenterPage() {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useContext(UiContext);

  const [page, setPage] = useState(1);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  const setBreadcrumbsOptions = useUiStore((state) => state.setBreadcrumbsOptions);

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Super Admin', to: '/super-admin' },
      { label: 'Help center', to: '/super-admin/help-center' },
    ]);
  }, [setBreadcrumbsOptions]);

  const { data: currentUser, isLoading: isLoadingUser } = useQuery('me', UserService.getMe);
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  const {
    data: ticketsResponse,
    isLoading: isLoadingTickets,
    isFetching: isFetchingTickets,
  } = useQuery(
    ['super-admin-support-tickets', page],
    () => SuperAdminService.getSupportTickets({ page, perPage: ITEMS_PER_PAGE }),
    {
      enabled: isSuperAdmin,
      keepPreviousData: true,
    }
  );

  const tickets = useMemo(() => ticketsResponse?.data || [], [ticketsResponse]);
  const paginationMeta = ticketsResponse?.meta;

  useEffect(() => {
    if (selectedTicketId) return;
    if (tickets.length) setSelectedTicketId(tickets[0].id);
  }, [tickets, selectedTicketId]);

  const { data: ticketDetail, isLoading: isLoadingTicketDetail } = useQuery(
    ['super-admin-support-ticket', selectedTicketId],
    () => SuperAdminService.getSupportTicket(selectedTicketId),
    {
      enabled: isSuperAdmin && !!selectedTicketId,
    }
  );

  const deleteMutation = useMutation(
    (ticketId) => SuperAdminService.deleteSupportTicket(ticketId),
    {
      onSuccess: async (_, ticketId) => {
        notifySuccess('Ticket deleted');

        queryClient.setQueryData(['super-admin-support-ticket', ticketId], null);
        queryClient.setQueryData(['super-admin-support-tickets', page], (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.filter((t) => t.id !== ticketId),
          };
        });

        setSelectedTicketId(null);
        await queryClient.invalidateQueries('super-admin-support-tickets');
      },
      onError: (error) => {
        notifyError(error?.response?.data?.message || 'Failed to delete ticket');
      },
    }
  );

  const replyMutation = useMutation(
    ({ ticketId, message }) => SuperAdminService.replySupportTicket(ticketId, { message }),
    {
      onSuccess: async () => {
        notifySuccess('Reply sent');
        setReplyMessage('');
        await queryClient.invalidateQueries(['super-admin-support-ticket', selectedTicketId]);
        await queryClient.invalidateQueries(['super-admin-support-tickets']);
      },
      onError: (error) => {
        notifyError(error?.response?.data?.message || 'Failed to send reply');
      },
    }
  );

  const handleReply = useCallback(() => {
    const trimmed = replyMessage.trim();
    if (!trimmed || !selectedTicketId || replyMutation.isLoading) return;
    replyMutation.mutate({ ticketId: selectedTicketId, message: trimmed });
  }, [replyMessage, selectedTicketId, replyMutation]);

  const handleDelete = useCallback(() => {
    if (!selectedTicketId || deleteMutation.isLoading) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm('Delete this ticket? This cannot be undone.');
    if (!confirmed) return;

    deleteMutation.mutate(selectedTicketId);
  }, [selectedTicketId, deleteMutation]);

  if (!isLoadingUser && !isSuperAdmin) {
    return <Navigate to="/my-videos" replace />;
  }

  let listContent = null;
  if (isLoadingTickets) {
    listContent = (
      <div className={classes.loadingState}>Loading tickets…</div>
    );
  } else if (!tickets.length) {
    listContent = (
      <div className={classes.emptyState}>No support tickets yet.</div>
    );
  } else {
    listContent = (
      <>
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <Th
                  classes={classes}
                  sortField={null}
                  sortOrder={null}
                  setSortField={() => {}}
                  setSortOrder={() => {}}
                  field="organization"
                  title="Organization"
                  noSort
                />
                <Th
                  classes={classes}
                  sortField={null}
                  sortOrder={null}
                  setSortField={() => {}}
                  setSortOrder={() => {}}
                  field="from"
                  title="From"
                  noSort
                />
                <Th
                  classes={classes}
                  sortField={null}
                  sortOrder={null}
                  setSortField={() => {}}
                  setSortOrder={() => {}}
                  field="message"
                  title="Message"
                  noSort
                />
                <Th
                  classes={classes}
                  sortField={null}
                  sortOrder={null}
                  setSortField={() => {}}
                  setSortOrder={() => {}}
                  field="updated"
                  title="Updated"
                  noSort
                />
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const isSelected = ticket.id === selectedTicketId;
                return (
                  <tr
                    key={ticket.id}
                    className={isSelected ? classes.selectedRow : classes.tableRow}
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <td className={classes.cell}>
                      {ticket.organization?.name || '-'}
                    </td>
                    <td className={classes.cell}>
                      <div className={classes.fromName}>{ticket.customerName}</div>
                      <div className={classes.fromEmail}>{ticket.customerEmail}</div>
                    </td>
                    <td className={classes.cell}>
                      {ticket.lastMessagePreview || '-'}
                    </td>
                    <td className={classes.cell}>
                      {ticket.lastActivityAt
                        ? new Date(ticket.lastActivityAt).toLocaleString()
                        : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={classes.paginationContainer}>
          <Pagination
            page={page}
            setPage={setPage}
            isLoading={isFetchingTickets}
            paginationMeta={paginationMeta}
          />
        </div>
      </>
    );
  }

  let detailContent = null;
  if (!selectedTicketId) {
    detailContent = (
      <div className={classes.emptyState}>Select a ticket to view details.</div>
    );
  } else if (isLoadingTicketDetail) {
    detailContent = (
      <div className={classes.loadingState}>Loading conversation…</div>
    );
  } else {
    detailContent = (
      <>
        <div className={classes.detailHeader}>
          <div className={classes.detailHeaderRow}>
            <div className={classes.detailTitle}>
              Ticket #{ticketDetail?.id}
            </div>
            <FilledButton
              onClick={handleDelete}
              isDisabled={deleteMutation.isLoading}
              color="#666666"
              style={{ height: 46, minWidth: 140, fontSize: 16, lineHeight: '20px', padding: '10px 12px' }}
            >
              {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
            </FilledButton>
          </div>
          <div className={classes.detailMeta}>
            <div>
              <strong>Org:</strong> {ticketDetail?.organization?.name || '-'}
            </div>
            <div>
              <strong>From:</strong> {ticketDetail?.customerName} ({ticketDetail?.customerEmail})
            </div>
          </div>
        </div>

        <div className={classes.messages}>
          {(ticketDetail?.messages || []).map((m) => (
            <div key={m.id} className={classes.message}>
              <div className={classes.messageHeader}>
                <span className={classes.sender}>
                  {m.senderType === 'super_admin' ? 'Support' : 'Customer'}
                </span>
                <span className={classes.timestamp}>
                  {m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}
                </span>
              </div>
              <div className={classes.messageBody}>{m.body}</div>
            </div>
          ))}
        </div>

        <div className={classes.reply}>
          <Textarea
            placeholder="Reply as support@snapsop.com…"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            disabled={replyMutation.isLoading || isLoadingTicketDetail}
          />
          <div className={classes.replyActions}>
            <FilledButton
              onClick={handleReply}
              isDisabled={!replyMessage.trim() || replyMutation.isLoading}
            >
              {replyMutation.isLoading ? 'Sending…' : 'Send reply'}
            </FilledButton>
          </div>
        </div>
      </>
    );
  }

  return (
    <Layout hasUserBlock>
      <div className={classes.SuperAdminHelpCenterPage}>
        <Navigation />

        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[
                { label: 'Super Admin', to: '/super-admin' },
                { label: 'Help center', to: '/super-admin/help-center' },
              ]}
            />
            <p className={classes.subtitle}>Customer support messages</p>
          </div>

          <div className={classes.content}>
            <div className={classes.list}>
              {listContent}
            </div>

            <div className={classes.detail}>
              {detailContent}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
